import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateQueryTypeRequestSchema,
  type CreateQueryTypeRequest,
  type QueryTypeDetail,
  type QueryTypeListItem,
  UpdateQueryTypeRequestSchema,
  type UpdateQueryTypeRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateQueryType, useUpdateQueryType } from '../use-query-types';

import { deriveCode, useAutoDeriveCode } from '@/components/master-code-field';

interface QueryTypeFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  queryType?: QueryTypeDetail | QueryTypeListItem | null;
}

const CREATE_DEFAULTS: CreateQueryTypeRequest = {
  code: '',
  name: '',
  description: '',
  sortOrder: 0,
  isActive: true,
};

/**
 * Field set shared by create + edit. Reads the form via context
 * (UzFormField → useFormContext), so it renders inside either UzForm.
 */
const QueryTypeFields = ({ pending }: { pending: boolean }): React.JSX.Element => (
  <>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. International Package"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="description">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Description"
          placeholder="Optional"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive types are hidden from lead forms."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const QueryTypeFormDrawer = ({
  open,
  onOpenChange,
  mode,
  queryType,
}: QueryTypeFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createQueryType = useCreateQueryType();
  const updateQueryType = useUpdateQueryType();
  const pending = createQueryType.isPending || updateQueryType.isPending;

  const createForm = useForm<CreateQueryTypeRequest>({
    resolver: zodResolver(CreateQueryTypeRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateQueryTypeRequest>({
    resolver: zodResolver(UpdateQueryTypeRequestSchema),
    defaultValues: {},
  });

  // Watches `name` and writes the derived code into form state so the
  // hidden `code` field is valid when zod validates the create payload.
  useAutoDeriveCode(createForm, isCreate);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (queryType) {
      // `code` is intentionally omitted from the edit form - it's the
      // stable machine identifier and is read-only post-create.
      updateForm.reset({
        name: queryType.name,
        description: queryType.description ?? '',
        sortOrder: queryType.sortOrder,
        isActive: queryType.isActive,
      });
    }
  }, [open, isCreate, queryType, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    // The code field is hidden in the UI; derive it from the name so
    // the operator can't accidentally create two query types with the
    // same machine code (the schema regex + server uniqueness check
    // still apply as a belt-and-braces guard).
    await createQueryType.mutateAsync({
      ...values,
      code: deriveCode(values.name),
      description: values.description?.trim() ? values.description : null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!queryType) return;
    await updateQueryType.mutateAsync({
      id: queryType.id,
      payload: {
        ...values,
        description: values.description?.trim() ? values.description : null,
      },
    });
    onOpenChange(false);
  });

  const fields = <QueryTypeFields pending={pending} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add query type' : `Edit ${queryType?.name ?? 'query type'}`}
      description="The name is what agents see."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
